<script lang="ts">
  import { cn } from '$lib/utils';
  import { ChevronRight } from 'lucide-svelte';

  type GrandchildLink = {
    id: string;
    title: string;
    href: string;
  };
  type ChildLinks = {
    id: string;
    title: string;
    href: string;
    links?: GrandchildLink[];
  };
  type Navigation = {
    id: string;
    title: string;
    href?: string;
    links: ChildLinks[];
  };
  export let navigation: Navigation[];
  export let currentPathName: string;
  export let currentUrlHash: string;

  let isMobileOpen: boolean;
  export let isDesktopOpen = false;

  const toggleMobileMenu = (open: boolean) => {
    isMobileOpen = open;
    return;
  };

  let currentPath: string;
  let openSections: Record<string, boolean> = {};

  $: {
    currentPath = currentPathName + currentUrlHash;
    navigation.forEach((section) => {
      openSections[section.id] = isOpen(section);
      section.links.forEach((link) => {
        openSections[link.id] = isOpen(link);
        if (link.links) {
          link.links.forEach((grandchild) => {
            openSections[grandchild.id] = isOpen(grandchild);
          });
        }
      });
    });
  }

  function isOpen(link: ChildLinks | GrandchildLink): boolean {
    return (
      link.href === currentPath ||
      link.links?.some(
        (child) =>
          child.href === currentPath ||
          child.links?.some((grandchild) => grandchild.href === currentPath)
      ) ||
      false
    );
  }

  function handleNavigationClick() {
    toggleMobileMenu(false);
  }

  const toggleSection = (id: string): void => {
    openSections[id] = !openSections[id];
  };
</script>

<div
  class={cn(
    {
      'fixed inset-0 h-full w-full': isMobileOpen,
      'hidden lg:block': !isMobileOpen
    },
    'lg:relative lg:block lg:flex-none z-40 bg-background border-r'
  )}
>
  <div
    class={cn(
      {
        'lg:block': isDesktopOpen,
        'lg:hidden': !isDesktopOpen
      },
      'sticky top-4 lg:top-[4.5rem] -ml-0.5 h-[calc(100vh-4.5rem)] overflow-y-auto overflow-x-hidden lg:py-16 pl-0.5'
    )}
  >
    <nav
      class="text-base lg:text-sm w-full md:pr-6 md:pl-6 lg:pl-0 pb-48 w-64 lg:pr-8 xl:w-72 xl:pr-16"
    >
      <ul class="divide-y md:divide-y-0 sm:space-y-4">
        {#each navigation as section (section.id)}
          <li class="px-5 md:px-0">
            <div class={cn('flex justify-between items-center')}>
              <h2
                class={cn('font-display font-medium py-6 sm:py-0', {
                  'text-primary': currentPathName === section.href
                })}
              >
                {#if section.href}
                  <a href={section.href} on:click={handleNavigationClick}>{section.title}</a>
                {:else}
                  {section.title}
                {/if}
              </h2>
              {#if section.links && section.links.length > 0}
                <button
                  class="cursor-pointer pointer-events-auto h-full pl-4"
                  on:click={() => {
                    toggleSection(section.id);
                  }}
                >
                  <ChevronRight
                    class={cn('h-6 w-6 sm:h-4 sm:w-4', {
                      'transform rotate-90': openSections[section.id],
                      'text-primary': isOpen(section)
                    })}
                    aria-hidden="true"
                  />
                </button>
              {/if}
            </div>
            {#if isOpen(section) || openSections[section.id]}
              <ul class={cn('mt-2 mb-4 md:mt-2 md:mb-0 space-y-2 lg:mt-4 lg:space-y-4', {})}>
                {#each section.links as link (link.id)}
                  <li
                    class={cn('relative border-l-2 -ml-0.5 py-4 sm:py-0', {
                      '': link.href !== currentPath,
                      'border-primary text-primary': link.href === currentPath
                    })}
                  >
                    <a
                      on:click={handleNavigationClick}
                      href={link.href}
                      class={cn('block w-full pl-3.5', {})}
                    >
                      {link.title}
                    </a>
                    {#if isOpen(link) || openSections[link.id]}
                      <ul class="pl-8 space-y-2">
                        {#each link.links as grandchild (grandchild.id)}
                          <li class="relative">
                            <a
                              on:click={handleNavigationClick}
                              href={grandchild.href}
                              class="block w-full pl-3.5"
                            >
                              {grandchild.title}
                            </a>
                          </li>
                        {/each}
                      </ul>
                    {/if}
                  </li>
                {/each}
              </ul>
            {/if}
          </li>
        {/each}
      </ul>
    </nav>
  </div>
</div>
<button
  on:click={() => toggleMobileMenu(!isMobileOpen)}
  class="fixed z-40 bottom-16 right-4 w-16 h-16 rounded-full bg-orange-600 text-white block lg:hidden shadow-xl drop-shadow-2xl"
  ><span class="sr-only">Open site navigation</span><svg
    width="24"
    height="24"
    fill="none"
    class="absolute top-1/2 left-1/2 -mt-3 -ml-3 transition duration-300 transform"
    ><path
      d="M4 8h16M4 16h16"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    /></svg
  ><svg
    width="24"
    height="24"
    fill="none"
    class="absolute top-1/2 left-1/2 -mt-3 -ml-3 transition duration-300 transform opacity-0 scale-80"
    ><path
      d="M6 18L18 6M6 6l12 12"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    /></svg
  ></button
>
